import moment from 'moment';

import {
  createFormFieldHeader,
  getCheckedCheckboxSVG,
  getElementLabel,
  getEmptyStarSVG,
  getFilledStarSVG,
  getFormElement,
  getHalfStarSVG,
  getOptionText,
  getSelectedRadioSVG,
  getUncheckedCheckboxSVG,
  getUnselectedRadioSVG,
  parseHtmlContent
} from '../utils';

export const renderRadioButtons = (formJson, value, fieldName) => {
  const element = getFormElement(formJson, fieldName);
  const label = getElementLabel(formJson, fieldName);

  const content = [];
  const header = createFormFieldHeader('Multiple Choice', label);
  if (header) {
    content.push(header);
  }

  if (element && element.options) {
    const optionsContainer = {
      stack: element.options.map((option) => {
        const isSelected = Array.isArray(value) ? value.includes(option.key) : value === option.key;
        return {
          table: {
            widths: [15, '*'],
            body: [
              [
                {
                  svg: isSelected ? getSelectedRadioSVG() : getUnselectedRadioSVG(),
                  width: 12,
                  border: [false, false, false, false]
                },
                {
                  text: option.text,
                  style: 'optionText',
                  border: [false, false, false, false]
                }
              ]
            ]
          },
          layout: 'noBorders',
          margin: [0, 0.5, 0, 0.5]
        };
      }),
      margin: [0, 2, 0, 0]
    };

    content.push(optionsContainer);
  }

  return content;
};

export const renderCheckboxes = (formJson, value, fieldName) => {
  const element = getFormElement(formJson, fieldName);
  const label = getElementLabel(formJson, fieldName);

  const content = [];
  const header = createFormFieldHeader('Checkboxes', label);
  if (header) {
    content.push(header);
  }

  if (element && element.options) {
    const optionsContainer = {
      stack: element.options.map((option) => {
        const isSelected = Array.isArray(value) && value.includes(option.key);
        return {
          table: {
            widths: [15, '*'],
            body: [
              [
                {
                  svg: isSelected ? getCheckedCheckboxSVG() : getUncheckedCheckboxSVG(),
                  width: 12,
                  border: [false, false, false, false]
                },
                {
                  text: option.text,
                  style: 'optionText',
                  border: [false, false, false, false]
                }
              ]
            ]
          },
          layout: 'noBorders',
          margin: [0, 0.5, 0, 0.5]
        };
      }),
      margin: [0, 2, 0, 0]
    };

    content.push(optionsContainer);
  }

  return content;
};

export const renderSignature = (formJson, value, fieldName) => {
  const label = getElementLabel(formJson, fieldName);

  const content = [];
  const header = createFormFieldHeader('Signature', label);
  if (header) {
    content.push(header);
  }

  if (value && value !== '' && value !== 'base64...') {
    let imageData = value;

    if (!value.startsWith('data:image/') && !value.startsWith('http')) {
      imageData = `data:image/png;base64,${value}`;
    }

    content.push({
      image: imageData,
      width: 250,
      height: 100,
      margin: [0, 3, 0, 0]
    });
  } else {
    content.push({
      text: 'No signature provided',
      style: 'placeholder',
      margin: [0, 3, 0, 0]
    });
  }

  return content;
};

export const renderCamera = (formJson, value, fieldName) => {
  const label = getElementLabel(formJson, fieldName);

  const content = [];
  const header = createFormFieldHeader('Camera', label);
  if (header) {
    content.push(header);
  }

  if (value && value !== '' && (value.startsWith('data:image/') || value.startsWith('http'))) {
    content.push({
      image: value,
      width: 400,
      margin: [0, 3, 0, 0]
    });
  } else {
    content.push({
      text: 'No photo captured',
      style: 'placeholder',
      margin: [0, 3, 0, 0]
    });
  }

  return content;
};

export const renderImage = (value) => {
  if (value && value !== '' && (value.startsWith('data:image/') || value.startsWith('http'))) {
    return {
      image: value,
      width: 400,
      margin: [0, 6, 0, 0]
    };
  }
  return null;
};

export const renderTextInput = (formJson, value, fieldName) => {
  const label = getElementLabel(formJson, fieldName);

  const content = [];
  const header = createFormFieldHeader('Text', label);
  if (header) {
    content.push(header);
  }

  content.push({
    table: {
      widths: ['*'],
      body: [
        [
          {
            text: parseHtmlContent(value) || '',
            style: 'fieldValue',
            border: [true, true, true, true],
            borderColor: ['#E5E7EB', '#E5E7EB', '#E5E7EB', '#E5E7EB']
          }
        ]
      ]
    },
    layout: {
      paddingLeft: () => 12,
      paddingRight: () => 12,
      paddingTop: () => 8,
      paddingBottom: () => 8,
      hLineWidth: () => 1,
      vLineWidth: () => 1,
      hLineColor: () => '#E5E7EB',
      vLineColor: () => '#E5E7EB'
    },
    margin: [0, 3, 0, 0]
  });

  return content;
};

export const renderTextArea = (formJson, value, fieldName) => {
  const label = getElementLabel(formJson, fieldName);

  const content = [];
  const header = createFormFieldHeader('Text Area', label);
  if (header) {
    content.push(header);
  }

  content.push({
    table: {
      widths: ['*'],
      body: [
        [
          {
            text: parseHtmlContent(value) || '',
            style: 'fieldValue',
            border: [true, true, true, true],
            borderColor: ['#E5E7EB', '#E5E7EB', '#E5E7EB', '#E5E7EB']
          }
        ]
      ]
    },
    layout: {
      paddingLeft: () => 12,
      paddingRight: () => 12,
      paddingTop: () => 8,
      paddingBottom: () => 8,
      hLineWidth: () => 1,
      vLineWidth: () => 1,
      hLineColor: () => '#E5E7EB',
      vLineColor: () => '#E5E7EB'
    },
    margin: [0, 3, 0, 0]
  });

  return content;
};

export const renderDropdown = (formJson, value, fieldName) => {
  const label = getElementLabel(formJson, fieldName);

  // Check if there's actually a value provided
  let displayText = '';
  if (value && value !== '' && value !== null && value !== undefined) {
    const selectedOption = getOptionText(getFormElement(formJson, fieldName), value);
    displayText = selectedOption || '';
  } else {
    displayText = 'No selection';
  }

  return [
    createFormFieldHeader('Dropdown', label),
    {
      table: {
        widths: ['*'],
        body: [
          [
            {
              text: displayText,
              style: displayText === 'No selection' ? 'placeholder' : 'fieldValue',
              border: [true, true, true, true],
              borderColor: ['#E5E7EB', '#E5E7EB', '#E5E7EB', '#E5E7EB']
            }
          ]
        ]
      },
      layout: {
        paddingLeft: () => 12,
        paddingRight: () => 12,
        paddingTop: () => 8,
        paddingBottom: () => 8,
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => '#E5E7EB',
        vLineColor: () => '#E5E7EB'
      },
      margin: [0, 3, 0, 0]
    }
  ];
};

export const renderTags = (formJson, value, fieldName) => {
  const label = getElementLabel(formJson, fieldName);

  const content = [];
  const header = createFormFieldHeader('Tags', label);
  if (header) {
    content.push(header);
  }

  if (Array.isArray(value) && value.length > 0) {
    const tags = value.map((tag) => tag.text || tag.label || tag.value || tag);

    const tagBoxes = tags.map((tagText) => ({
      table: {
        widths: ['auto'],
        body: [
          [
            {
              text: tagText,
              style: 'fieldValue',
              border: [true, true, true, true],
              borderColor: ['#E5E7EB', '#E5E7EB', '#E5E7EB', '#E5E7EB']
            }
          ]
        ]
      },
      layout: {
        paddingLeft: () => 8,
        paddingRight: () => 8,
        paddingTop: () => 4,
        paddingBottom: () => 4,
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => '#E5E7EB',
        vLineColor: () => '#E5E7EB'
      }
    }));

    const tagsContainer = {
      table: {
        widths: Array(tagBoxes.length).fill('auto'),
        body: [tagBoxes]
      },
      layout: {
        paddingLeft: () => 0,
        paddingRight: () => 6,
        paddingTop: () => 0,
        paddingBottom: () => 0,
        hLineWidth: () => 0,
        vLineWidth: () => 0
      },
      margin: [0, 3, 0, 0]
    };

    content.push(tagsContainer);
  } else {
    // Show "No tags selected" when there's no data
    content.push({
      text: 'No tags selected',
      style: 'placeholder',
      margin: [0, 3, 0, 0]
    });
  }

  return content;
};

export const renderRating = (formJson, value, fieldName) => {
  const label = getElementLabel(formJson, fieldName);

  const content = [];
  const header = createFormFieldHeader('Rating', label);
  if (header) {
    content.push(header);
  }

  const rating = parseFloat(value) || 0;

  const stars = [];
  for (let i = 1; i <= 5; i++) {
    let starSVG;
    if (i <= Math.floor(rating)) {
      // Full star
      starSVG = getFilledStarSVG();
    } else if (i === Math.floor(rating) + 1 && rating % 1 >= 0.5) {
      // Half star
      starSVG = getHalfStarSVG();
    } else {
      // Empty star
      starSVG = getEmptyStarSVG();
    }

    stars.push({
      svg: starSVG,
      width: 16,
      border: [false, false, false, false]
    });
  }

  const ratingContainer = {
    table: {
      widths: [18, 18, 18, 18, 18, 'auto'],
      body: [
        [
          ...stars,
          {
            text: ` (${rating}/5)`,
            style: 'fieldValue',
            border: [false, false, false, false],
            margin: [16, 2, 0, 0]
          }
        ]
      ]
    },
    layout: {
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 0,
      paddingBottom: () => 0,
      hLineWidth: () => 0,
      vLineWidth: () => 0
    },
    margin: [0, 3, 5, 0]
  };

  content.push(ratingContainer);

  return content;
};

export const renderDatePicker = (formJson, value, fieldName) => {
  const label = getElementLabel(formJson, fieldName);

  // Check if there's actually a value provided
  if (!value || value === '' || value === null || value === undefined) {
    return [
      createFormFieldHeader('Date', label),
      {
        text: '-',
        style: 'placeholder',
        margin: [0, 3, 0, 0]
      }
    ];
  }

  return [
    createFormFieldHeader('Date', label),
    {
      text: `${moment(value).format('dddd, MMMM DD, YYYY')}`,
      style: 'fieldValue',
      margin: [0, 3, 0, 0]
    }
  ];
};

export const renderRange = (formJson, value, fieldName) => {
  const label = getElementLabel(formJson, fieldName);
  const element = getFormElement(formJson, fieldName);

  const minValue = element?.min_value || 0;
  const maxValue = element?.max_value || 100;
  const minLabel = element?.min_label || '';
  const maxLabel = element?.max_label || '';

  // Check if there's actually a value provided
  if (!value || value === '' || value === null || value === undefined) {
    return [
      createFormFieldHeader('Range', label),
      {
        text: 'No value selected',
        style: 'placeholder',
        margin: [0, 3, 0, 0]
      }
    ];
  }

  const currentValue = parseInt(value) || 0;
  const percentage = ((currentValue - minValue) / (maxValue - minValue)) * 100;
  const trackWidth = 120;
  const fillWidth = (trackWidth * percentage) / 100;

  const sliderSVG = `<svg width="140" height="20" viewBox="0 0 140 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Track background -->
    <rect x="10" y="8" width="${trackWidth}" height="4" rx="2" fill="#E5E7EB"/>
    <!-- Filled portion -->
    <rect x="10" y="8" width="${fillWidth}" height="4" rx="2" fill="#0D3B50"/>
    <!-- Handle -->
    <circle cx="${10 + fillWidth}" cy="10" r="6" fill="#0D3B50" stroke="white" stroke-width="2"/>
  </svg>`;

  return [
    createFormFieldHeader('Range', label),
    {
      table: {
        widths: ['auto', 'auto', 'auto', '*', 'auto'],
        body: [
          [
            {
              text: minLabel || '',
              style: 'placeholder',
              border: [false, false, false, false],
              margin: [0, 2, 8, 0]
            },
            {
              svg: sliderSVG,
              width: 140,
              border: [false, false, false, false]
            },
            {
              text: maxLabel || '',
              style: 'placeholder',
              border: [false, false, false, false],
              margin: [8, 2, 0, 0]
            },
            {
              text: '',
              border: [false, false, false, false]
            },
            {
              text: `${currentValue}/${maxValue}`,
              style: 'fieldValue',
              bold: true,
              border: [false, false, false, false],
              alignment: 'right'
            }
          ]
        ]
      },
      layout: 'noBorders',
      margin: [0, 3, 0, 0]
    }
  ];
};
